import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Card, Loader } from "@/components/Common";
import { PhotoGrid } from "@/components/analysis/PhotoStep";
import ResultCard from "@/components/analysis/ResultCard";
import ResultSummary from "@/components/analysis/ResultSummary";
import SecondOpinion from "@/components/analysis/SecondOpinion";
import { STATUS_LABEL, ASSESS_LABEL, FOLLOWUP_LABEL } from "@/pages/Consultations";

const RegistryInfo = ({ items }) =>
  items.length > 0 && (
    <Card className="mt-3" testid="pro-registry-info">
      <p className="eyebrow">Informazione tecnica – non è un'indicazione terapeutica</p>
      <p className="mb-2 text-xs text-[#5b6b60]">Sostanze ipotizzate dall'AI e loro presenza nel registro ministeriale (qualsiasi coltura e impiego). Non mostrate all'utente.</p>
      {items.map((s) => (
        <p key={s.substance} className="text-sm" data-testid={`pro-registry-${s.substance}`}>
          <strong>{s.substance}</strong> · {s.verified ? `${s.authorized_products} prodotti autorizzati · dataset del ${fmtDate(s.dataset_date)}` : "registro non disponibile"}
        </p>
      ))}
    </Card>
  );

const EMPTY = { ai_assessment: "confermata", professional_diagnosis: "", kb_id: "", recommendations: "", private_notes: "", followup: [], outcome: "", status: "in_valutazione" };

function ReviewForm({ cid, kb, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (v) => setF({ ...f, followup: f.followup.includes(v) ? f.followup.filter((x) => x !== v) : [...f.followup, v] });
  const save = async () => {
    try {
      await api.post(`/pro/cases/${cid}/review`, { ...f, kb_id: f.kb_id || null });
      toast.success("Valutazione professionale registrata");
      setF(EMPTY);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Card testid="pro-review-form" accent>
      <p className="eyebrow">Valutazione professionale</p>
      <h3 className="font-serif text-xl font-semibold text-[#0D2517]">Diagnosi della Dott.ssa</h3>
      <div className="mt-3 grid grid-cols-2 gap-2">
        {Object.entries(ASSESS_LABEL).map(([k, l]) => (
          <button key={k} onClick={() => setF({ ...f, ai_assessment: k })} className={`chip !text-xs ${f.ai_assessment === k ? "chip-on" : ""}`} data-testid={`review-assessment-${k}`}>{l.replace("Analisi AI ", "")}</button>
        ))}
      </div>
      <select className="field mt-3" value={f.kb_id} onChange={set("kb_id")} data-testid="review-kb-select">
        <option value="">Causa (voce base di conoscenza, facoltativa)</option>
        {kb.map((k) => <option key={k.id} value={k.id}>{k.name} – {k.type_label}</option>)}
      </select>
      <textarea className="field mt-2 min-h-[70px]" placeholder="Diagnosi professionale" value={f.professional_diagnosis} onChange={set("professional_diagnosis")} data-testid="review-diagnosis" />
      <textarea className="field mt-2 min-h-[90px]" placeholder="Indicazioni per l'utente" value={f.recommendations} onChange={set("recommendations")} data-testid="review-recommendations" />
      <textarea className="field mt-2 min-h-[60px]" placeholder="Note riservate (non visibili all'utente)" value={f.private_notes} onChange={set("private_notes")} data-testid="review-private-notes" />
      <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-[#5b6b60]">Il caso richiede</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {Object.entries(FOLLOWUP_LABEL).map(([k, l]) => <button key={k} onClick={() => toggle(k)} className={`chip !text-xs ${f.followup.includes(k) ? "chip-on" : ""}`} data-testid={`review-followup-${k}`}>{l}</button>)}
      </div>
      <input className="field mt-3" placeholder="Esito del caso" value={f.outcome} onChange={set("outcome")} data-testid="review-outcome" />
      <select className="field mt-2" value={f.status} onChange={set("status")} data-testid="review-status-select">
        {Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <button onClick={save} className="btn-primary mt-4 w-full" data-testid="review-save-button">REGISTRA VALUTAZIONE</button>
    </Card>
  );
}

const ms = (v) => (v == null ? "–" : `${(v / 1000).toFixed(1)} s`);
const ROUND_ROWS = [["load_photos_ms", "Lettura foto"], ["plantnet_species_ms", "Pl@ntNet specie"], ["plantnet_diseases_ms", "Pl@ntNet Diseases"],
  ["gpt_ms", "Analisi GPT"], ["checks_ms", "Controlli e risposta finale"], ["total_ms", "Totale elaborazione"]];

function Timings({ t }) {
  return (
    <Card testid="pro-case-timings">
      <p className="eyebrow">Tempi misurati</p>
      {t.upload && <p className="text-sm">Caricamento foto ({t.upload.photos}): dispositivo {ms(t.upload.client_ms)} · server {ms(t.upload.server_ms)} · {Math.round((t.upload.bytes_received || 0) / 1024)} KB</p>}
      {["round1", "round2"].filter((k) => t[k]).map((k) => (
        <div key={k} className="mt-2" data-testid={`pro-timings-${k}`}>
          <p className="text-xs font-semibold uppercase text-[#5b6b60]">{k === "round1" ? "Prima risposta" : "Dopo le risposte"} · {t[k].model}</p>
          {ROUND_ROWS.map(([key, label]) => <p key={key} className="text-sm">{label}: {ms(t[k][key])}</p>)}
          {t[k].gpt_tokens && <p className="text-xs text-[#5b6b60]">Token GPT: {t[k].gpt_tokens.input_tokens} in · {t[k].gpt_tokens.output_tokens} out</p>}
        </div>
      ))}
      {t.consult && (
        <p className="mt-2 text-sm" data-testid="pro-timings-consult">Consulenza completa: {ms(t.consult.gpt_ms)} · tentativo {t.consult.attempt}
          {t.consult.gpt_tokens && ` · token ${t.consult.gpt_tokens.input_tokens} in · ${t.consult.gpt_tokens.output_tokens} out`}</p>
      )}
    </Card>
  );
}

export default function ProCase() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/pro/cases/${id}`).then(({ data }) => setD(data)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loader />;
  const { consultation: c, analysis: a, plant } = d;
  const ident = a.identification;

  return (
    <div className="space-y-4 px-5 py-6" data-testid="pro-case-page">
      <div>
        <p className="eyebrow">Caso · {STATUS_LABEL[c.status]}</p>
        <h1 className="font-serif text-3xl font-semibold text-[#0D2517]">{c.user_name}</h1>
        <p className="text-xs text-[#5b6b60]">{c.user_email}{c.phone && ` · ${c.phone}`} · inviato il {fmtDate(c.created_at)} · consenso al trasferimento del {fmtDate(c.consent_at)}</p>
        {c.message && <p className="mt-2 rounded-xl bg-white p-3 text-sm italic">"{c.message}"</p>}
      </div>

      <Card testid="pro-case-photos"><p className="eyebrow">Fotografie</p><PhotoGrid photos={a.photos} /></Card>

      <Card testid="pro-case-identification">
        <p className="eyebrow">Identificazione</p>
        <p className="text-sm">Specie confermata: <strong>{a.species?.common_name}</strong> <span className="botanical">{a.species?.botanical_name}</span> · fonte: {a.species?.source}</p>
        {ident && <p className="mb-3 text-xs text-[#5b6b60]">Attendibilità identificazione: {ident.confidence?.level} · {ident.confidence?.reasons?.join("; ")}</p>}
        {ident && <SecondOpinion ident={ident} pro />}
        {plant && <p className="mt-1 text-xs text-[#5b6b60]">Pianta: {plant.name} · {plant.cultivation || "coltivazione n.d."} · {plant.location || ""}</p>}
      </Card>

      <Card testid="pro-case-anamnesis">
        <p className="eyebrow">Anamnesi (dichiarato dall'utente)</p>
        {(a.user_declared?.length ?? 0) ? a.user_declared.map((q, i) => <p key={i} className="text-sm"><span className="text-[#5b6b60]">{q.question}</span> <strong>{q.answer}</strong></p>) : <p className="text-sm text-[#5b6b60]">Nessuna risposta.</p>}
      </Card>

      {a.result && (
        <div>
          <p className="eyebrow">Risposta originale dell'AI (non modificabile)</p>
          {a.result.plan_version === 3 ? (
            <div className="space-y-4"><ResultSummary result={a.result} species={a.species} consult={a.consult} pro /></div>
          ) : <ResultCard result={a.result} species={a.species} />}
          <RegistryInfo items={a.phyto_registry_info || a.result.phyto?.substances || []} />
        </div>
      )}

      {a.timings && <Timings t={a.timings} />}

      {d.plant_history.length > 0 && (
        <Card testid="pro-case-history">
          <p className="eyebrow">Storico della pianta</p>
          {d.plant_history.map((h) => <p key={h.id} className="text-sm">{fmtDate(h.created_at)} · {h.result?.main_cause?.name || "n.d."} ({h.result?.confidence || "-"})</p>)}
          {d.interventions.map((i) => <p key={i.id} className="text-xs text-[#5b6b60]">{fmtDate(i.date)} · {i.type}: {i.description}</p>)}
        </Card>
      )}

      {d.reviews.length > 0 && (
        <Card testid="pro-case-reviews">
          <p className="eyebrow">Valutazioni registrate (archivio separato)</p>
          {d.reviews.map((r) => (
            <div key={r.id} className="mb-3 border-l-2 border-[#C9A96E] pl-3 text-sm">
              <p className="text-xs text-[#8a958d]">{fmtDate(r.created_at)} · {ASSESS_LABEL[r.ai_assessment]} · {STATUS_LABEL[r.status]}</p>
              {r.professional_diagnosis && <p className="font-semibold">{r.professional_diagnosis}</p>}
              {r.recommendations && <p>{r.recommendations}</p>}
              {r.private_notes && <p className="text-xs italic text-[#5b6b60]">Note riservate: {r.private_notes}</p>}
              {r.followup?.length > 0 && <p className="text-xs">Richiede: {r.followup.map((f) => FOLLOWUP_LABEL[f]).join(", ")}</p>}
              {r.outcome && <p className="text-xs">Esito: {r.outcome}</p>}
            </div>
          ))}
        </Card>
      )}

      <ReviewForm cid={c.id} kb={d.kb} onSaved={load} />
    </div>
  );
}
